import { defineStore } from 'pinia';
import { ManualTranslationPayloadItem, ManualTranslationsPayload } from '@/entities/checklist';

interface TranslationField {
  value: string;
  previous: string;
}

interface Translation {
  node_id: string;
  uuid: string;
  title?: TranslationField;
  htmlTitle?: TranslationField;
}

interface State {
  checklistId?: number;
  title?: TranslationField;
  description?: TranslationField;
  translations: Map<string, Translation>;
}

export const useManualTranslationsStore = defineStore('manualTranslations', {
  state: (): State => ({
    translations: new Map(),
  }),
  actions: {
    setChecklistId(id: number) {
      this.checklistId = id;
    },
    setTitle(value: string, previous: string) {
      if (value === previous) {
        this.title = undefined;
      } else {
        this.title = { value, previous };
      }
    },
    setDescription(value: string, previous: string) {
      if (value === previous) {
        this.description = undefined;
      } else {
        this.description = { value, previous };
      }
    },
    setTranslationTitle(node_id: string, uuid: string, value: string, previous: string) {
      const key = `${node_id}_${uuid}`;

      if (value === previous) {
        this.translations.delete(key);

        return;
      }

      const existing = this.translations.get(key);

      if (existing) {
        existing.title = { value, previous };

        return;
      }

      this.translations.set(key, {
        node_id,
        uuid,
        title: { value, previous },
      });
    },
    setTranslationHtmlTitle(node_id: string, uuid: string, value: string, previous: string) {
      const key = `${node_id}_${uuid}`;

      if (value === previous) {
        this.translations.delete(key);

        return;
      }

      const existing = this.translations.get(key);

      if (existing) {
        existing.htmlTitle = { value, previous };

        return;
      }

      this.translations.set(key, {
        node_id,
        uuid,
        htmlTitle: { value, previous },
      });
    },
    getTitle(node_id: string, uuid: string): string | null {
      const key = `${node_id}_${uuid}`;
      const existing = this.translations.get(key);

      return existing?.title?.value ?? null;
    },
    getPayload(checklistStatus: number): ManualTranslationsPayload {
      const payload: ManualTranslationsPayload = {
        checklistStatus,
        translations: [],
      };

      if (this.title !== undefined) {
        payload.title = this.title.value;
      }

      if (this.description !== undefined) {
        payload.description = this.description.value;
      }

      this.translations.forEach(translation => {
        const item: ManualTranslationPayloadItem = {
          node_id: translation.node_id,
          uuid: translation.uuid,
        };

        if (translation.title !== undefined) {
          item.title = translation.title.value;
        }

        if (translation.htmlTitle !== undefined) {
          item.htmlTitle = translation.htmlTitle.value;
        }

        payload.translations.push(item);
      });

      return payload;
    },
  },
});
